// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO COMÚN DE PANELES DE ANÁLISIS
// Única fuente de verdad para colores, umbrales, fechas y componentes UI
// compartidos por: AnalisisAsitentas, AnalisisSupervisoras, AnalisisPisos,
// AnalisisAlumnos.
// ═══════════════════════════════════════════════════════════════════════════
import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

// ── FECHAS ──────────────────────────────────────────────────────────────────
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const lunesISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
  return d.toISOString().slice(0, 10);
};
export const addDays = (iso, n) => {
  const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
};
export const diffDays = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

// Selector de período ESTÁNDAR (idéntico en todos los paneles)
export const PERIODOS = [
  { key: "semana",  label: "Esta semana" },
  { key: "sem_ant", label: "Sem. ant." },
  { key: "mes",     label: "Este mes" },
  { key: "mes_ant", label: "Mes ant." },
  { key: "3meses",  label: "3 meses" },
  { key: "custom",  label: "Personalizado" },
];

export function calcPeriodo(key) {
  const d = new Date(), hoy = todayISO(), lun = lunesISO();
  if (key === "semana")  return { desde: lun, hasta: hoy };
  if (key === "sem_ant") return { desde: addDays(lun, -7), hasta: addDays(lun, -1) };
  if (key === "mes") {
    return { desde: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`, hasta: hoy };
  }
  if (key === "mes_ant") {
    const pm = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const um = new Date(d.getFullYear(), d.getMonth(), 0);
    return { desde: pm.toISOString().slice(0, 10), hasta: um.toISOString().slice(0, 10) };
  }
  if (key === "3meses") {
    const ini = new Date(d.getFullYear(), d.getMonth() - 2, 1);
    return { desde: ini.toISOString().slice(0, 10), hasta: hoy };
  }
  return { desde: lun, hasta: hoy };
}

export function prevPeriodo({ desde, hasta }) {
  const n = diffDays(desde, hasta) + 1;
  const prevHasta = addDays(desde, -1);
  const prevDesde = addDays(prevHasta, -(n - 1));
  return { desde: prevDesde, hasta: prevHasta };
}

export const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch (_) { return iso; }
};

export const fmtEuro = (n) =>
  (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// ── SEMÁFORO (umbrales ESTÁNDAR: verde≥80, amarillo≥60, rojo<60) ─────────────
export const C  = { verde: "#16a34a", amarillo: "#d97706", rojo: "#dc2626", gris: "#94a3b8", azul: "#2563eb", indigo: "#6366f1" };
export const BG = { verde: "#dcfce7", amarillo: "#fef3c7", rojo: "#fee2e2", gris: "#f1f5f9", azul: "#dbeafe", indigo: "#e0e7ff" };

export function semaforo(pct) {
  if (pct === null || pct === undefined) return "gris";
  return pct >= 80 ? "verde" : pct >= 60 ? "amarillo" : "rojo";
}
export const colorPct = (pct) => C[semaforo(pct)];

// Banderas por código de país
export const BANDERA = {
  ES: "🇪🇸", IT: "🇮🇹", FR: "🇫🇷", DE: "🇩🇪", PT: "🇵🇹", GB: "🇬🇧", UK: "🇬🇧", US: "🇺🇸",
  MX: "🇲🇽", AR: "🇦🇷", CO: "🇨🇴", PE: "🇵🇪", CL: "🇨🇱", BR: "🇧🇷", CN: "🇨🇳",
  JP: "🇯🇵", KR: "🇰🇷", IN: "🇮🇳", PL: "🇵🇱", RO: "🇷🇴", UA: "🇺🇦", MA: "🇲🇦",
  NL: "🇳🇱", BE: "🇧🇪", CH: "🇨🇭", AT: "🇦🇹", IE: "🇮🇪", SE: "🇸🇪", NO: "🇳🇴",
  RU: "🇷🇺", TR: "🇹🇷", GR: "🇬🇷", EC: "🇪🇨", VE: "🇻🇪", UY: "🇺🇾", PY: "🇵🇾",
};
export const banderaDe = (pais) => BANDERA[(pais || "").toUpperCase()] || "🌍";

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES ATOM (estandarizados)
// ═══════════════════════════════════════════════════════════════════════════

// Donut de progreso. size por defecto 52. color opcional (si no, semáforo).
export const MiniDonut = ({ pct, size = 52, color }) => {
  const col = color || C[semaforo(pct)];
  const r = (size - 10) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = pct != null ? Math.max(0, Math.min(1, pct / 100)) * circ : 0;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={5} />
      {dash > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={5}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray 0.5s" }} />
      )}
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize={pct != null ? size * 0.21 : size * 0.17} fontWeight={800} fill={col}>
        {pct != null ? `${pct}%` : "—"}
      </text>
    </svg>
  );
};

// Flecha de tendencia (delta en puntos %)
export const Tendencia = ({ delta, unidad = "%" }) => {
  if (delta === null || delta === undefined) return null;
  if (Math.abs(delta) <= 3) return <span style={{ color: C.gris, fontSize: 12, fontWeight: 700 }}>→ estable</span>;
  if (delta > 0) return <span style={{ color: C.verde, fontSize: 12, fontWeight: 700 }}>↑ +{delta}{unidad}</span>;
  return <span style={{ color: C.rojo, fontSize: 12, fontWeight: 700 }}>↓ {delta}{unidad}</span>;
};

export const SemaforoBadge = ({ tipo, label }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    background: BG[tipo] || BG.gris, color: C[tipo] || C.gris,
    border: `1.5px solid ${C[tipo] || C.gris}`,
    borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700,
  }}>
    {tipo === "verde" ? "🟢" : tipo === "amarillo" ? "🟡" : tipo === "rojo" ? "🔴" : "⬜"}
    {" "}{label}
  </span>
);

// ── ESTADO (semáforo de 4 niveles según reportes hechos + resultados) ─────────
// critico 🔴 · atencion 🟡 · bien 🟢 · sin_reportes ⬜ (no hay información)
export const ESTADO = {
  critico:      { key: "critico",      emoji: "🔴", label: "Crítico",      color: C.rojo,     bg: BG.rojo },
  atencion:     { key: "atencion",     emoji: "🟡", label: "Atención",     color: C.amarillo, bg: BG.amarillo },
  bien:         { key: "bien",         emoji: "🟢", label: "Bien",         color: C.verde,    bg: BG.verde },
  sin_reportes: { key: "sin_reportes", emoji: "⬜", label: "Sin reportes", color: C.gris,     bg: BG.gris },
};
export const ORDEN_ESTADO = { critico: 0, atencion: 1, sin_reportes: 2, bien: 3 };

export const EstadoBadge = ({ estado, sub }) => {
  const e = ESTADO[estado] || ESTADO.sin_reportes;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: e.bg, color: e.color, border: `1.5px solid ${e.color}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>
      {e.emoji} {e.label}{sub ? ` · ${sub}` : ""}
    </span>
  );
};

// Barra resumen de semáforo: 4 pastillas clicables que filtran la lista.
// counts = { critico, atencion, bien, sin_reportes }; filtro/setFiltro controlan el filtro activo.
export const SemaforoResumen = ({ counts, filtro, setFiltro, sinReportesLabel }) => (
  <>
    <div style={{ display: "flex", gap: 8, padding: "4px 14px 0", flexWrap: "wrap" }}>
      {["critico", "atencion", "bien", "sin_reportes"].map((k) => {
        const e = ESTADO[k]; const activo = filtro === k; const n = counts[k] || 0;
        return (
          <button key={k} onClick={() => setFiltro(activo ? null : k)}
            style={{
              flex: "1 1 0", minWidth: 110, cursor: "pointer", textAlign: "left",
              background: activo ? e.color : e.bg, color: activo ? "#fff" : e.color,
              border: `1.5px solid ${e.color}`, borderRadius: 12, padding: "8px 12px",
              opacity: n === 0 ? 0.5 : 1, transition: "all .12s",
            }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{n}</div>
            <div style={{ fontSize: 11.5, fontWeight: 700, marginTop: 2 }}>{e.emoji} {k === "sin_reportes" && sinReportesLabel ? sinReportesLabel : e.label}</div>
          </button>
        );
      })}
    </div>
    {filtro && (
      <div style={{ padding: "6px 14px 0", fontSize: 12, color: "#64748b" }}>
        Filtrando por <strong>{ESTADO[filtro].label}</strong>. <span onClick={() => setFiltro(null)} style={{ color: C.indigo, cursor: "pointer", fontWeight: 700 }}>Quitar filtro</span>
      </div>
    )}
  </>
);

export const Chip = ({ emoji, label, color = "#475569", bg = "#f1f5f9" }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, background: bg, color,
    borderRadius: 12, padding: "3px 9px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
  }}>
    {emoji && <span>{emoji}</span>}{label}
  </span>
);

export const ProgresoBarra = ({ pct, color, height = 8 }) => (
  <div style={{ background: "#e2e8f0", borderRadius: 99, height, overflow: "hidden", flex: 1 }}>
    <div style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.5s" }} />
  </div>
);

// Barra de score semáforo con conteos (verde/amarillo/rojo/desconozco)
export const ScoreBar = ({ label, emoji, verde = 0, amarillo = 0, rojo = 0, desconozco = 0, total }) => {
  const tot = total != null ? total : verde + amarillo + rojo + desconozco;
  if (!tot) return null;
  const p = (n) => (n / tot) * 100;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>
        {emoji} {label}
      </div>
      <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", background: "#f3f4f6" }}>
        <div style={{ width: `${p(verde)}%`, background: C.verde }} title={`Bien: ${verde}`} />
        <div style={{ width: `${p(amarillo)}%`, background: C.amarillo }} title={`Regular: ${amarillo}`} />
        <div style={{ width: `${p(rojo)}%`, background: C.rojo }} title={`Mal: ${rojo}`} />
        {desconozco > 0 && <div style={{ width: `${p(desconozco)}%`, background: "#cbd5e1" }} title={`Desconoce: ${desconozco}`} />}
      </div>
      <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2, display: "flex", gap: 6 }}>
        {verde > 0 && <span>🟢 {verde}</span>}
        {amarillo > 0 && <span>🟡 {amarillo}</span>}
        {rojo > 0 && <span>🔴 {rojo}</span>}
        {desconozco > 0 && <span>⚪ {desconozco}</span>}
      </div>
    </div>
  );
};

// Tarjeta KPI compacta
export const KpiCard = ({ icon, label, value, sub, color = "#1e293b", bg = "#f8fafc" }) => (
  <div style={{ background: bg, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
    {icon && <span style={{ fontSize: 26, flexShrink: 0 }}>{icon}</span>}
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// CHROME COMPARTIDO (header, período, búsqueda, estados)
// ═══════════════════════════════════════════════════════════════════════════

export const Header = ({ icon, title, count, countLabel, onBack, right }) => (
  <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
    <button onClick={onBack} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
      ← Volver
    </button>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Análisis del período</div>
      <div style={{ fontSize: 18, fontWeight: 800 }}>{icon} {title}</div>
    </div>
    {right != null ? right : (count != null && (
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", textAlign: "right" }}>
        {count} {countLabel}
      </div>
    ))}
  </div>
);

export const PeriodoBar = ({ periodoKey, setPeriodoKey, custom, setCustom }) => (
  <>
    <div style={{ display: "flex", gap: 6, padding: "10px 14px", overflowX: "auto", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
      {PERIODOS.map(p => (
        <button key={p.key} onClick={() => setPeriodoKey(p.key)} style={{
          padding: "7px 14px", borderRadius: 20,
          border: periodoKey === p.key ? "1.5px solid #1e293b" : "1.5px solid #e2e8f0",
          background: periodoKey === p.key ? "#1e293b" : "#f8fafc",
          color: periodoKey === p.key ? "#fff" : "#475569",
          fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
        }}>
          {p.label}
        </button>
      ))}
    </div>
    {periodoKey === "custom" && (
      <div style={{ display: "flex", gap: 10, padding: "8px 16px", alignItems: "center", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Desde</label>
        <input type="date" value={custom.desde} max={custom.hasta}
          onChange={e => setCustom(c => ({ ...c, desde: e.target.value }))}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        <label style={{ fontSize: 13, fontWeight: 600 }}>Hasta</label>
        <input type="date" value={custom.hasta} min={custom.desde} max={todayISO()}
          onChange={e => setCustom(c => ({ ...c, hasta: e.target.value }))}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
      </div>
    )}
  </>
);

export const BarraBusqueda = ({ busqueda, setBusqueda, orden, setOrden, ordenes, placeholder = "🔍 Buscar..." }) => (
  <div style={{ display: "flex", gap: 8, padding: "12px 14px", flexWrap: "wrap" }}>
    <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder={placeholder}
      style={{ flex: 1, minWidth: 160, padding: "9px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none" }} />
    <select value={orden} onChange={e => setOrden(e.target.value)}
      style={{ padding: "9px 10px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, background: "#fff", fontWeight: 600, color: "#475569" }}>
      {ordenes.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  </div>
);

// Estados de carga / error / vacío
export const EstadoCarga = ({ loading, error, vacio, onRetry, vacioTexto = "No hay datos para el período seleccionado" }) => {
  if (loading) return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>⏳</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>Cargando datos…</div>
    </div>
  );
  if (error) return (
    <div style={{ margin: "16px 14px", background: "#fee2e2", color: "#b91c1c", padding: "14px 16px", borderRadius: 12, display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 24 }}>❌</span>
      <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{error}</div>
      {onRetry && <button onClick={onRetry} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 700, cursor: "pointer" }}>Reintentar</button>}
    </div>
  );
  if (vacio) return (
    <div style={{ textAlign: "center", padding: "50px 20px", color: "#94a3b8" }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>🔍</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{vacioTexto}</div>
    </div>
  );
  return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// BANNER "⚡ ATENCIÓN" — resumen ejecutivo (lo que requiere atención de un vistazo)
// items: [{ icon, texto, severidad: 'rojo'|'amarillo'|'verde', n }]
// ═══════════════════════════════════════════════════════════════════════════
export const AtencionBanner = ({ items, okTexto = "Todo en orden, sin alertas." }) => {
  const reales = (items || []).filter(Boolean);
  const hayAlerta = reales.some(i => i.severidad === "rojo" || i.severidad === "amarillo");
  const soloInfo  = reales.length > 0 && !hayAlerta;
  if (reales.length === 0) {
    return (
      <div style={{ margin: "12px 14px", background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>✅</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#15803d" }}>{okTexto}</span>
      </div>
    );
  }
  const bannerBg     = hayAlerta ? "#fffbeb" : soloInfo ? "#f8fafc" : "#f0fdf4";
  const bannerBorder = hayAlerta ? "#fcd34d" : soloInfo ? "#cbd5e1" : "#86efac";
  const titColor     = hayAlerta ? "#92400e" : "#475569";
  const titIcon      = hayAlerta ? "⚡ Requiere atención" : "ℹ️ Información";
  return (
    <div style={{ margin: "12px 14px", borderRadius: 14, padding: "12px 14px", background: bannerBg, border: `1.5px solid ${bannerBorder}` }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: titColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {titIcon}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {reales.map((it, i) => {
          const clickable = typeof it.onClick === "function";
          return (
            <div key={i}
              onClick={clickable ? it.onClick : undefined}
              style={{ display: "flex", alignItems: "center", gap: 10, borderRadius: 8, padding: "4px 6px 4px 0",
                cursor: clickable ? "pointer" : "default",
                background: clickable ? "transparent" : "transparent",
              }}>
              <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                background: BG[it.severidad] || BG.gris, fontSize: 15,
              }}>{it.icon}</span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "#374151" }}>{it.texto}</span>
              {it.n != null && (
                <span style={{ fontSize: 13, fontWeight: 900, minWidth: 26, textAlign: "center", color: C[it.severidad] || C.gris }}>{it.n}</span>
              )}
              {clickable && <span style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }}>›</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SHELL DE MODAL CON PESTAÑAS (centrado, scrollable)
// ═══════════════════════════════════════════════════════════════════════════
export const cs = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px",
  },
  modal: {
    background: "#fff", borderRadius: 18, width: "100%", maxWidth: 620, maxHeight: "90vh",
    display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
  mHead: { display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 18px", borderBottom: "1px solid #e2e8f0" },
  tabsBar: { display: "flex", gap: 4, padding: "8px 12px", borderBottom: "1px solid #e2e8f0", overflowX: "auto", background: "#f8fafc" },
  body: { padding: "16px 18px", overflowY: "auto", flex: 1 },
  sec: { background: "#f8fafc", borderRadius: 14, padding: "14px 16px", border: "1px solid #eef2f7" },
  sh: { margin: "0 0 10px", fontSize: 14, fontWeight: 800, color: "#334155" },
};

export const ModalShell = ({ titulo, subtitulo, colorBorde = C.gris, tabs, tab, setTab, onClose, children }) => {
  // Bloquea el scroll del fondo mientras el modal está abierto
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
  <div style={cs.overlay} onClick={onClose}>
    <div style={cs.modal} onClick={e => e.stopPropagation()}>
      <div style={{ ...cs.mHead, borderLeft: `5px solid ${colorBorde}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titulo}</div>
          {subtitulo && <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>{subtitulo}</div>}
        </div>
        <button onClick={onClose} style={{ background: "#f1f5f9", border: "none", borderRadius: 10, width: 34, height: 34, fontSize: 17, cursor: "pointer", color: "#475569", flexShrink: 0 }}>✕</button>
      </div>
      {tabs && tabs.length > 1 && (
        <div style={cs.tabsBar}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "8px 12px", borderRadius: 10, border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
              background: tab === t.key ? "#1e293b" : "transparent",
              color: tab === t.key ? "#fff" : "#64748b",
            }}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div style={cs.body}>{children}</div>
    </div>
  </div>
  );
};

// Contenedor de página estándar
export const PageWrap = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', system-ui, sans-serif", paddingBottom: 60 }}>
    {children}
  </div>
);

// Banner de comparación con período anterior
export const ComparaBanner = ({ prev }) => (
  <div style={{ margin: "0", padding: "7px 16px", background: "#eff6ff", borderBottom: "1px solid #dbeafe", fontSize: 12, color: "#1d4ed8", fontWeight: 600 }}>
    📊 Comparando con período anterior: {fmtFecha(prev.desde)} → {fmtFecha(prev.hasta)}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// GRAFO DE ENLACES — dibujo de la red de afinidad de un piso (layout circular)
// alumnos: [{ id_alumno, nombre, aislado }]
// enlaces: [{ a, b, superlink }]  (a,b = id_alumno)
// ═══════════════════════════════════════════════════════════════════════════
export const GrafoEnlaces = ({ alumnos = [], enlaces = [], size = 300 }) => {
  if (!alumnos.length) {
    return <div style={{ textAlign: "center", color: C.gris, padding: 20, fontSize: 13 }}>Sin alumnos</div>;
  }
  const cx = size / 2, cy = size / 2;
  const R = size / 2 - 38;
  const n = alumnos.length;
  // posición de cada nodo en el círculo
  const pos = {};
  alumnos.forEach((a, i) => {
    const ang = (2 * Math.PI * i) / n - Math.PI / 2;
    pos[a.id_alumno] = {
      x: cx + (n === 1 ? 0 : R) * Math.cos(ang),
      y: cy + (n === 1 ? 0 : R) * Math.sin(ang),
      a,
    };
  });
  const inicial = (nom) => (nom ? nom.trim()[0].toUpperCase() : "?");
  const primerNombre = (nom) => (nom ? nom.split(" ")[0] : "—");
  return (
    <svg width="100%" viewBox={`0 0 ${size} ${size}`} style={{ maxWidth: size, display: "block", margin: "0 auto" }}>
      {/* aristas */}
      {enlaces.map((e, i) => {
        const p1 = pos[e.a], p2 = pos[e.b];
        if (!p1 || !p2) return null;
        return (
          <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={e.superlink ? "#7c3aed" : "#fb7185"}
            strokeWidth={e.superlink ? 3.5 : 2}
            strokeOpacity={e.superlink ? 0.95 : 0.65}
            strokeDasharray={e.superlink ? "0" : "0"} />
        );
      })}
      {/* nodos */}
      {alumnos.map((a) => {
        const p = pos[a.id_alumno];
        const aislado = a.aislado;
        return (
          <g key={a.id_alumno}>
            <circle cx={p.x} cy={p.y} r={16}
              fill={aislado ? "#fee2e2" : "#ede9fe"}
              stroke={aislado ? C.rojo : "#7c3aed"} strokeWidth={aislado ? 2.5 : 2} />
            <text x={p.x} y={p.y + 1} textAnchor="middle" dominantBaseline="middle"
              fontSize={13} fontWeight={800} fill={aislado ? C.rojo : "#5b21b6"}>{inicial(a.nombre)}</text>
            <text x={p.x} y={p.y + 30} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="#475569">
              {primerNombre(a.nombre)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Leyenda del grafo
export const LeyendaGrafo = () => (
  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
    <span><span style={{ display: "inline-block", width: 16, height: 2, background: "#fb7185", verticalAlign: "middle", marginRight: 4 }} />Enlace</span>
    <span><span style={{ display: "inline-block", width: 16, height: 3, background: "#7c3aed", verticalAlign: "middle", marginRight: 4 }} />Superlink ⭐</span>
    <span><span style={{ display: "inline-block", width: 11, height: 11, borderRadius: "50%", border: `2px solid ${C.rojo}`, background: "#fee2e2", verticalAlign: "middle", marginRight: 4 }} />Aislado</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// SERIE TEMPORAL — gráfico de evolución (línea). data: [{ x, ...series }]
// series: [{ key, label, color }]
// ═══════════════════════════════════════════════════════════════════════════
export const SerieTemporal = ({ data = [], series = [], height = 180, xKey = "x", titulo }) => {
  if (!data || data.length < 2) {
    return (
      <div style={{ textAlign: "center", color: C.gris, padding: "26px 10px", fontSize: 13 }}>
        📈 Aún no hay suficiente histórico para mostrar la evolución
      </div>
    );
  }
  return (
    <div>
      {titulo && <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>{titulo}</div>}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 6, right: 10, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
          <RTooltip />
          {series.map(s => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
              stroke={s.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
